'use client';
import React, { useEffect, useMemo, useState } from 'react';
import { CardRoot } from '../Card/Card';
import { Image } from '@/hen-ui/atomics/Image';
import { Heading } from '@/hen-ui/atomics/Heading';
import { Text } from '@/hen-ui/atomics/Text';
import { Badge } from '@/hen-ui/atomics/Badge';
import { Button } from '@/hen-ui/atomics/Button';
import { Icon } from '@/hen-ui/atomics/Icon';
import { cn } from '@/hen-ui/utils';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';

/**
 * Props for the BrandCard component
 */
export interface BrandCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Image source for the brand logo.
   * Rendered using the <Image /> atomic component.
   * @required
   */
  logoSrc: string;

  /**
   * Alt text for the brand logo image.
   * Important for accessibility.
   * @required
   */
  logoAlt: string;

  /**
   * Brand name or title.
   * Rendered using the <Heading /> atomic component.
   * @required
   */
  title: string;

  /**
   * Descriptive text content about the brand.
   * Rendered as children using the <Text /> atomic component.
   * @required
   */
  children: React.ReactNode;

  /**
   * Array of category tags.
   * Each tag is rendered as a <Badge /> atomic component.
   * @optional
   */
  tags?: string[];

  /**
   * Destination URL for the "Visit Site" call-to-action.
   * Rendered using the <Button /> atomic component with asChild pattern.
   * @optional
   */
  siteUrl?: string;

  /**
   * URL for the "Read more" link.
   * If provided, displays a "Read more" link at the bottom of the card.
   * @optional
   */
  readMoreUrl?: string;

  /**
   * Card background variant.
   * @default 'background-white'
   */
  variant?: 'background-white' | 'background-gray' | 'background-transparent';

  /**
   * Custom class name for the card container.
   */
  className?: string;
}

/**
 * BrandCard - A composite component for displaying brand information
 *
 * A visually rich card component that displays brand information in a consistent,
 * accessible, and design-system-compliant way. Composed entirely of EDU atomic
 * components following HEN Design System standards.
 *
 * ## Component Composition
 * - **Card**: Base container with design system variants
 * - **Image**: Brand logo display (64x64px)
 * - **Heading**: Brand name/title (h4 variant)
 * - **Text**: Descriptive content with truncation
 * - **Badge**: Category tags (light variant)
 * - **Button**: "Visit Site" CTA and expand/collapse control (textOnly variant)
 * - **Icon**: Chevron icon for expand/collapse indicator
 *
 * ## Features
 * - Fully composed from EDU atomic components
 * - Token-driven styling (no hardcoded values)
 * - Expandable content with "Read more"/"Collapse" functionality
 * - Smart truncation for descriptions (3 lines) and tags (responsive)
 * - Minimum height of 384px (min-h-96)
 * - WCAG 2.1 AA accessible
 * - Responsive layout
 * - Semantic HTML structure
 * - Keyboard navigable
 *
 * ## Accessibility
 * - Proper alt text for logo images
 * - Semantic heading hierarchy (h4)
 * - Keyboard-accessible buttons with proper ARIA labels
 * - `aria-expanded` state for expand/collapse button
 * - Sufficient color contrast (4.5:1 text, 3:1 UI)
 * - Screen reader friendly structure
 *
 * @example
 * // Basic usage with all features
 * <BrandCard
 *   logoSrc="/logos/brand.png"
 *   logoAlt="Brand Logo"
 *   title="BestColleges.com"
 *   siteUrl="https://bestcolleges.com"
 *   readMoreUrl="#"
 * >
 *   Lorem ipsum dolor sit amet consectetur. Aliquam dignissim pulvinar
 *   mattis at velit porttitor.
 * </BrandCard>
 *
 * @example
 * // With tags and read more link
 * <BrandCard
 *   logoSrc="/logos/psychology.png"
 *   logoAlt="Psychology.org Logo"
 *   title="Psychology.org"
 *   tags={['General Education', 'Business & Management', 'Counseling']}
 *   siteUrl="https://psychology.org"
 *   readMoreUrl="#"
 * >
 *   Lorem ipsum dolor sit amet consectetur. Aliquam dignissim pulvinar
 *   mattis at velit porttitor.
 * </BrandCard>
 *
 * @example
 * // Multi-column layout (recommended usage)
 * <div className="columns-1 md:columns-2 gap-6">
 *   <div className="break-inside-avoid mb-6">
 *     <BrandCard
 *       logoSrc="/logos/brand1.png"
 *       logoAlt="Brand 1 Logo"
 *       title="Brand 1"
 *       tags={['Education', 'Healthcare']}
 *       siteUrl="https://example.com"
 *       readMoreUrl="#"
 *     >
 *       Description text...
 *     </BrandCard>
 *   </div>
 *   <div className="break-inside-avoid mb-6">
 *     <BrandCard
 *       logoSrc="/logos/brand2.png"
 *       logoAlt="Brand 2 Logo"
 *       title="Brand 2"
 *       tags={['Business', 'Management']}
 *       siteUrl="https://example.com"
 *       readMoreUrl="#"
 *     >
 *       Description text...
 *     </BrandCard>
 *   </div>
 * </div>
 */
export const BrandCard = React.forwardRef<HTMLDivElement, BrandCardProps>(
  (
    {
      logoSrc,
      logoAlt,
      title,
      children,
      tags,
      siteUrl,
      readMoreUrl,
      variant = 'background-gray',
      className,
      ...props
    },
    ref,
  ) => {
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [showExtraDesc, setShowExtraDesc] = React.useState(false);
    const [showExtraTags, setShowExtraTags] = React.useState(false);
    const [hasInteracted, setHasInteracted] = React.useState(false);
    const [maxTagsLength, setMaxTagsLength] = React.useState(67);
    const [clampCharCount, setClampCharCount] = React.useState(180);

    React.useEffect(() => {
      const updateResponsiveValues = () => {
        const width = window.innerWidth;
        if (width < 640) {
          setMaxTagsLength(40);
          setClampCharCount(110); // ~3 lines for mobile
        } else if (width < 768) {
          setMaxTagsLength(50);
          setClampCharCount(200); // ~3 lines for small tablets
        } else if (width < 1164) {
          setMaxTagsLength(55);
          setClampCharCount(170); // ~3 lines for tablets/small desktop
        } else {
          setMaxTagsLength(67);
          setClampCharCount(200); // ~3 lines for large desktop
        }
      };
      updateResponsiveValues();
      window.addEventListener('resize', updateResponsiveValues);
      return () => window.removeEventListener('resize', updateResponsiveValues);
    }, []);

    // Avoid incomplete words and add ellipsis ---
    const descriptionText = typeof children === 'string' ? children : '';
    let clampedText = descriptionText;
    let extraText = '';

    if (descriptionText.length > clampCharCount) {
      // Find last space before clampCharCount
      const lastSpaceIdx = descriptionText.lastIndexOf(' ', clampCharCount);
      clampedText = descriptionText.slice(
        0,
        lastSpaceIdx > 0 ? lastSpaceIdx : clampCharCount,
      );
      extraText = descriptionText.slice(
        lastSpaceIdx > 0 ? lastSpaceIdx : clampCharCount,
      );
    }

    // Tag truncation logic
    const MORE_TAG_LENGTH = 7;
    const visibleTags: string[] = [];
    let totalLength = 0;
    if (tags && tags.length > 0) {
      for (let i = 0; i < tags.length; i++) {
        const tagLength = tags[i].length;
        const lengthWithGap = totalLength + tagLength + (i > 0 ? 2 : 0);
        if (
          lengthWithGap + 2 + MORE_TAG_LENGTH <= maxTagsLength &&
          i < tags.length - 1
        ) {
          visibleTags.push(tags[i]);
          totalLength = lengthWithGap;
        } else if (i === tags.length - 1 && lengthWithGap <= maxTagsLength) {
          visibleTags.push(tags[i]);
        } else {
          break;
        }
      }
    }
    const extraTags =
      tags && tags.length > visibleTags.length
        ? tags.slice(visibleTags.length)
        : [];

    // Animation toggle logic
    const handleToggle = () => {
      if (!hasInteracted) setHasInteracted(true);
      if (isExpanded) {
        setShowExtraDesc(true);
        setShowExtraTags(true);
        setIsExpanded(false);
        setTimeout(() => {
          setShowExtraDesc(false);
          setShowExtraTags(false);
        }, 500);
      } else {
        setShowExtraDesc(true);
        setShowExtraTags(true);
        setIsExpanded(true);
      }
    };

    return (
      <CardRoot
        ref={ref}
        variant={variant}
        data-hen-id={COMPONENT_IDS.BrandCard}
        className={cn(
          'relative border border-neutral-200 flex flex-col transition-all duration-300 ease-in-out overflow-hidden gap-xxs py-7 px-8 md:min-h-96',
          className,
        )}
        {...props}
      >
        {/* CTA Button */}
        {siteUrl && (
          <div className='absolute top-6 right-6 z-10'>
            <Button variant='primaryFill' size='lg' asChild>
              <a href={siteUrl} target='_blank' rel='noopener noreferrer'>
                Visit Site
              </a>
            </Button>
          </div>
        )}

        <div className='flex flex-col gap-5 w-full'>
          {/* Logo Section */}
          <div className='w-16 h-16 flex items-center justify-center'>
            <Image
              src={logoSrc}
              alt={logoAlt}
              className='w-full h-full object-contain'
              width={64}
              height={64}
            />
          </div>

          {/* Title Section */}
          <Heading variant='h4' className='text-neutral-900'>
            {title}
          </Heading>

          {/* Description */}
          <Text variant='b4' className='text-neutral-700'>
            <span>
              {clampedText}
              {!isExpanded && extraText && '...'}
              {/* When expanded, replace ... with a line break for smooth transition */}
              {isExpanded && extraText && <br />}
            </span>
            {extraText && showExtraDesc && (
              <span
                className={isExpanded ? 'animate-fadein' : 'animate-fadeout'}
              >
                {extraText}
              </span>
            )}
          </Text>

          {/* Tags Section */}
          {tags && tags.length > 0 && (
            <div className='flex flex-col gap-3'>
              <Text
                variant='b4'
                tag='span'
                className='font-semibold text-neutral-900'
              >
                Focusing on
              </Text>
              <div className='flex gap-2 flex-wrap'>
                {/* Always Visible tags */}
                {visibleTags.map((tag) => (
                  <Badge
                    key={tag}
                    variant='light'
                    size='sm'
                    className='whitespace-nowrap text-neutral-900'
                  >
                    {tag}
                  </Badge>
                ))}

                {/* Fade in/out More... badge */}
                {extraTags.length > 0 && !showExtraTags && (
                  <Badge
                    variant='light'
                    size='sm'
                    className={cn(
                      'whitespace-nowrap text-neutral-900',
                      hasInteracted
                        ? !isExpanded && !showExtraTags
                          ? 'animate-fadein'
                          : isExpanded && showExtraTags
                            ? 'animate-fadeout'
                            : ''
                        : '',
                    )}
                  >
                    More...
                  </Badge>
                )}
                {/* Animated extra tags */}
                {extraTags.length > 0 &&
                  showExtraTags &&
                  extraTags.map((tag) => (
                    <Badge
                      key={tag}
                      variant='light'
                      size='sm'
                      className={cn(
                        'whitespace-nowrap text-neutral-900',
                        isExpanded ? 'animate-fadein' : 'animate-fadeout',
                      )}
                    >
                      {tag}
                    </Badge>
                  ))}
              </div>
            </div>
          )}
        </div>

        {/* Read More Link */}
        {readMoreUrl && (extraText || extraTags.length > 0) && (
          <Button
            variant='textOnly'
            size='sm'
            onClick={handleToggle}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? 'Collapse content' : 'Show more content'}
            className='pl-0 transition-all duration-500 ease-in-out'
          >
            {isExpanded ? 'Collapse' : 'Read more'}
            <Icon
              icon='chevron-down'
              size='sm'
              className={cn(
                'transition-transform duration-500 ease-in-out',
                isExpanded ? 'rotate-180' : '',
              )}
              aria-hidden='true'
            />
          </Button>
        )}
      </CardRoot>
    );
  },
);

BrandCard.displayName = 'BrandCard';
